import { getCookie, setCookie } from './cookies';
import {
  COUNT_DAYS_SAVE_COOKIE,
  IMPERSONATE_ADMIN_ID,
  KEY_APPSFLYER_UID,
  KEY_STORAGE_TARGET_URL,
  KEY_STORAGE_TARGET_URL_LOCK_WRITE,
  SESSION_STORAGE_IMPERSONATE_ACTIVE,
} from '../constants';

const TARGET_URL_WRITE_LOCK_MS = 60_000;

const addDaysDate = (countDay: number, date = new Date()): Date => {
  return new Date(date.setDate(date.getDate() + +countDay));
};

/** Единый lock: не писать `target_url` 60 с после записи/перезаписи, FB и impersonate. */
export const setTargetUrlWriteLock = (): void => {
  const until = new Date(Date.now() + TARGET_URL_WRITE_LOCK_MS);
  setCookie(KEY_STORAGE_TARGET_URL_LOCK_WRITE, '1', until);
};

const UTM_SOURCE_KEY = 'utm_source';
const UTM_MEDIUM_KEY = 'utm_medium';
const UTM_CAMPAIGN_KEY = 'utm_campaign';
const UTM_TEMPLATE_KEY = 'utm_template';
/** Короткие метки в SMS/push для functional link `/f/...`. */
const SHORT_TEMPLATE_KEY = 't';
const SHORT_CAMPAIGN_KEY = 'c';
const REFERRER_PARAM_KEY = 'referrer';
const CTIME_PARAM_KEY = 'ctime';

const isFunctionalLinkPath = (pathname: string): boolean => {
  return pathname === '/f' || pathname.startsWith('/f/');
};

/**
 * Functional link: `t`/`c` → `utm_template`/`utm_campaign` в адресной строке (replaceState).
 * Не выдумывает utm_source/utm_medium и не трогает уже заданные полные метки.
 * Возвращает true, если URL изменился.
 */
export const expandFunctionalLinkShortUtm = (): boolean => {
  try {
    const currentPageUrl = new URL(window.location.href);
    if (!isFunctionalLinkPath(currentPageUrl.pathname)) {
      return false;
    }

    const params = currentPageUrl.searchParams;
    const shortTemplate = params.get(SHORT_TEMPLATE_KEY)?.trim() ?? '';
    const shortCampaign = params.get(SHORT_CAMPAIGN_KEY)?.trim() ?? '';
    let changed = false;

    if (shortTemplate) {
      if (!params.get(UTM_TEMPLATE_KEY)?.trim()) {
        params.set(UTM_TEMPLATE_KEY, shortTemplate);
      }
      params.delete(SHORT_TEMPLATE_KEY);
      changed = true;
    }

    if (shortCampaign) {
      if (!params.get(UTM_CAMPAIGN_KEY)?.trim()) {
        params.set(UTM_CAMPAIGN_KEY, shortCampaign);
      }
      params.delete(SHORT_CAMPAIGN_KEY);
      changed = true;
    }

    if (!changed) {
      return false;
    }

    const nextUrl = `${currentPageUrl.pathname}${currentPageUrl.search}${currentPageUrl.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
    return true;
  } catch (error: unknown) {
    console.error(error);
    return false;
  }
};

/** Id шаблона/кампании для API: полные метки или короткие `t`/`c`. */
export const getFunctionalLinkTrackingIds = (
  searchParams: URLSearchParams,
): { templateId: string | null; campaignId: string | null } => {
  const readNonEmptyParam = (key: string): string | null => {
    const value = searchParams.get(key)?.trim();
    if (!value) {
      return null;
    }
    return value;
  };

  return {
    templateId: readNonEmptyParam(UTM_TEMPLATE_KEY) ?? readNonEmptyParam(SHORT_TEMPLATE_KEY),
    campaignId: readNonEmptyParam(UTM_CAMPAIGN_KEY) ?? readNonEmptyParam(SHORT_CAMPAIGN_KEY),
  };
};

/** Query с полными метками для редиректа с functional link (например на /user/account). */
export const buildFunctionalLinkUtmSearch = (templateId: string | null, campaignId: string | null): string => {
  const params = new URLSearchParams();

  if (templateId) {
    params.set(UTM_TEMPLATE_KEY, templateId);
  }
  if (campaignId) {
    params.set(UTM_CAMPAIGN_KEY, campaignId);
  }

  const query = params.toString();
  return query ? `?${query}` : '';
};

/** UTC время для `ctime`: как ISO 8601, но с суффиксом `+00:00` вместо `Z`. */
const formatCtimeUtcWithOffset = (date: Date = new Date()): string => {
  return date.toISOString().replace(/Z$/, '+00:00');
};

export interface UtmParamsFromStorage {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  ctime: string;
  appsflyerUid?: string;
}

const getNonEmptyStorageValue = (key: string): string | null => {
  const value = localStorage.getItem(key);
  if (!value) {
    return null;
  }
  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
};

/**
 * UTM из localStorage (мобильный webview: нативка кладёт ключи до загрузки SPA).
 * Нужны все три: utm_source, utm_medium, utm_campaign; опционально appsflyer_uid.
 */
export const getUtmParamsFromStorage = (): UtmParamsFromStorage | null => {
  try {
    const utmSource = getNonEmptyStorageValue(UTM_SOURCE_KEY);
    const utmMedium = getNonEmptyStorageValue(UTM_MEDIUM_KEY);
    const utmCampaign = getNonEmptyStorageValue(UTM_CAMPAIGN_KEY);
    const appsflyerUid = getNonEmptyStorageValue(KEY_APPSFLYER_UID);
    const ctime = getNonEmptyStorageValue(CTIME_PARAM_KEY);

    if (!utmSource || !utmMedium || !utmCampaign || !ctime) {
      return null;
    }

    return {
      utmSource,
      utmMedium,
      utmCampaign,
      ctime,
      ...(appsflyerUid && { appsflyerUid }),
    };
  } catch {
    return null;
  }
};

const buildTargetUrlWithUtmFromStorage = (baseUrl: URL, p: UtmParamsFromStorage): string => {
  const targetUrl = new URL(`${baseUrl.origin}${baseUrl.pathname}`);
  targetUrl.searchParams.set(UTM_SOURCE_KEY, p.utmSource);
  targetUrl.searchParams.set(UTM_MEDIUM_KEY, p.utmMedium);
  targetUrl.searchParams.set(UTM_CAMPAIGN_KEY, p.utmCampaign);
  targetUrl.searchParams.set(CTIME_PARAM_KEY, p.ctime);
  if (p.appsflyerUid) {
    targetUrl.searchParams.set(KEY_APPSFLYER_UID, p.appsflyerUid);
  }
  return targetUrl.href;
};

/**
 * Домены поисковиков → organic: utm_source=<google|yandex|bing|…>&utm_medium=organic&utm_campaign=organic (+ referrer & ctime в persist).
 */
const SEARCH_ENGINE_HOST_ROOTS: Record<string, string> = {
  'bing.com': 'bing',
  'yandex.ru': 'yandex',
  'yandex.com': 'yandex',
  'ya.ru': 'yandex',
  'yahoo.com': 'yahoo',
  'duckduckgo.com': 'duckduckgo',
  'brave.com': 'brave',
};

const GOOGLE_NON_ORGANIC_PREFIXES = [
  'analytics.',
  'myaccount.',
  'mail.',
  'drive.',
  'docs.',
  'calendar.',
  'accounts.',
  'developers.',
  'play.',
];

const normalizeHost = (host: string): string => {
  return host.replace(/^www\./i, '').toLowerCase();
};

const isGoogleOrganicHost = (h: string): boolean => {
  if (GOOGLE_NON_ORGANIC_PREFIXES.some(prefix => h.startsWith(prefix))) {
    return false;
  }
  if (h === 'google.com' || h.endsWith('.google.com')) {
    return true;
  }
  return /^google\.[a-z]{2}(\.[a-z]{2})?$/.test(h);
};

const getOrganicUtmSourceFromReferrerHostname = (hostname: string): string | null => {
  const h = normalizeHost(hostname);

  if (isGoogleOrganicHost(h)) {
    return 'google';
  }

  for (const [root, source] of Object.entries(SEARCH_ENGINE_HOST_ROOTS)) {
    if (h === root || h.endsWith(`.${root}`)) {
      return source;
    }
  }

  return null;
};

const getReferrerParamValue = (currentPageUrl: URL): string => {
  const raw = document.referrer?.trim() ?? '';
  if (!raw || raw === 'undefined') {
    return 'null';
  }

  try {
    const refUrl = new URL(raw);
    if (refUrl.origin === currentPageUrl.origin) {
      return 'null';
    }
    return normalizeHost(refUrl.hostname);
  } catch {
    return 'null';
  }
};

/**
 * Добавляет `referrer` и `ctime` к строке URL (п.1 и п.3 ТЗ).
 * `referrerParam` — опционально, если значение уже вычислено снаружи.
 */
export const enrichTargetUrlWithReferrerAndCtime = (href: string, referrerParam?: string): string => {
  const targetUrl = new URL(href);
  const currentPageUrl = new URL(window.location.href);
  const referrerValue = referrerParam ?? getReferrerParamValue(currentPageUrl);
  targetUrl.searchParams.set(REFERRER_PARAM_KEY, referrerValue);
  if (!targetUrl.searchParams.has(CTIME_PARAM_KEY)) {
    targetUrl.searchParams.set(CTIME_PARAM_KEY, formatCtimeUtcWithOffset());
  }
  return targetUrl.href;
};

export const persistTargetUrlToCookie = (baseHref: string): string => {
  const countDay = addDaysDate(COUNT_DAYS_SAVE_COOKIE);
  const currentPageUrl = new URL(window.location.href);
  const referrerValue = getReferrerParamValue(currentPageUrl);
  const enriched = enrichTargetUrlWithReferrerAndCtime(baseHref, referrerValue);
  setCookie(KEY_STORAGE_TARGET_URL, enriched, countDay);
  setTargetUrlWriteLock();
  return enriched;
};

const isImpersonateSession = (): boolean => {
  try {
    if (sessionStorage.getItem(SESSION_STORAGE_IMPERSONATE_ACTIVE) === '1') {
      return true;
    }
    const id = localStorage.getItem(IMPERSONATE_ADMIN_ID);
    return Boolean(id?.trim());
  } catch {
    return false;
  }
};

const hasImpersonateTokenInUrl = (): boolean => {
  try {
    const raw = new URL(window.location.href).searchParams.get('impersonate');
    if (!raw) {
      return false;
    }
    const parsed = JSON.parse(raw) as { token?: string };
    return Boolean(parsed?.token);
  } catch {
    return false;
  }
};

const buildDirectSyntheticHref = (origin: string): string => {
  const u = new URL(`${origin}/`);
  u.searchParams.set(UTM_SOURCE_KEY, 'direct');
  u.searchParams.set(UTM_MEDIUM_KEY, 'direct');
  u.searchParams.set(UTM_CAMPAIGN_KEY, 'direct');
  return u.href;
};

const buildSyntheticHrefWithoutUtmInAddressBar = (currentPageUrl: URL): string => {
  const origin = currentPageUrl.origin;
  const raw = document.referrer?.trim() ?? '';

  if (!raw || raw === 'undefined') {
    return buildDirectSyntheticHref(origin);
  }

  let refUrl: URL;
  try {
    refUrl = new URL(raw);
  } catch {
    return buildDirectSyntheticHref(origin);
  }

  if (refUrl.origin === currentPageUrl.origin) {
    return buildDirectSyntheticHref(origin);
  }

  const organicSource = getOrganicUtmSourceFromReferrerHostname(refUrl.hostname);
  if (organicSource) {
    const u = new URL(`${origin}/`);
    u.searchParams.set(UTM_SOURCE_KEY, organicSource);
    u.searchParams.set(UTM_MEDIUM_KEY, 'organic');
    u.searchParams.set(UTM_CAMPAIGN_KEY, 'organic');
    return u.href;
  }

  const refHost = normalizeHost(refUrl.hostname);
  const u = new URL(`${origin}/`);
  u.searchParams.set(UTM_SOURCE_KEY, 'referral');
  u.searchParams.set(UTM_MEDIUM_KEY, 'referral');
  u.searchParams.set(UTM_CAMPAIGN_KEY, refHost);
  return u.href;
};

const getCurrentPageHrefWithoutHash = (): string => {
  const u = new URL(window.location.href);
  u.hash = '';
  return u.href;
};

/**
 * П.2.1: UTM-разметка в URL — utm_source / utm_medium / utm_campaign,
 * либо utm_template (functional link после разворота t→utm_template).
 */
const hasIncomingUtmMarkingInUrl = (searchParams: URLSearchParams): boolean => {
  return (
    Boolean(searchParams.get(UTM_SOURCE_KEY)?.trim()) ||
    Boolean(searchParams.get(UTM_MEDIUM_KEY)?.trim()) ||
    Boolean(searchParams.get(UTM_CAMPAIGN_KEY)?.trim()) ||
    Boolean(searchParams.get(UTM_TEMPLATE_KEY)?.trim())
  );
};

/**
 * П.1: перезапись / полный URL — при наличии UTM в строке запроса (см. hasIncomingUtmMarkingInUrl).
 * П.2: без UTM в URL и пустой cookie → localStorage (webview), иначе synthetic по document.referrer.
 * П.3: `referrer` + `ctime` в строке target_url (через persist).
 * Lock `target_url_lock_write` на 60 с после persist; impersonate/FB — тот же lock (см. checks).
 *
 * Functional link после разворота t/c: в cookie уходит реальный URL с utm_template/utm_campaign
 * без подстановки выдуманных utm_source/utm_medium.
 */
export const setTargetUrl = (): void => {
  try {
    const currentPageUrl = new URL(window.location.href);
    const params = currentPageUrl.searchParams;

    if (getCookie(KEY_STORAGE_TARGET_URL_LOCK_WRITE)) {
      return;
    }

    if (isImpersonateSession() || hasImpersonateTokenInUrl()) {
      return;
    }

    if (params.has('ndl')) {
      return;
    }

    if (params.has('fb_redirect_code')) {
      return;
    }

    if (params.has('target_url')) {
      return;
    }

    const existingCookie = getCookie(KEY_STORAGE_TARGET_URL);
    const cookieEmpty = !existingCookie?.trim();
    const hasUtmInUrl = hasIncomingUtmMarkingInUrl(params);

    if (!cookieEmpty && !hasUtmInUrl) {
      return;
    }

    let baseHref: string | null = null;

    if (cookieEmpty) {
      if (hasUtmInUrl) {
        // Реальные метки (в т.ч. utm_template/utm_campaign) — без дописывания source/medium.
        baseHref = getCurrentPageHrefWithoutHash();
      } else {
        const utmFromStorage = getUtmParamsFromStorage();
        if (utmFromStorage) {
          baseHref = buildTargetUrlWithUtmFromStorage(currentPageUrl, utmFromStorage);
        } else {
          baseHref = buildSyntheticHrefWithoutUtmInAddressBar(currentPageUrl);
        }
      }
    } else if (hasUtmInUrl) {
      baseHref = getCurrentPageHrefWithoutHash();
    }

    if (!baseHref) {
      return;
    }

    persistTargetUrlToCookie(baseHref);
  } catch (error: unknown) {
    console.error(error);
  }
};
